import { useCallback } from 'react';
import { useQuery } from '@apollo/client';

import { OutstandingAgreementType } from '__generated__/GQL';
import asBentoModule from 'common/bento/hoc/asBentoModule';
import withStateMachine, {
  BentoModuleComponent,
} from 'common/bento/hoc/withStateMachine/withStateMachine';
import assertNever from 'common/bento/lib/assertNever';
import stringifyState from 'common/bento/lib/stringifyState';
import Agreement from 'common/bento/modules/Agreement';
import { BentoModuleDoneStatus } from 'common/bento/types/BentoModule';
import useCompanyContext from 'common/hooks/useCompanyContext';
import SuspenseQuery from 'components/SuspenseQuery';
import NavigationContext from 'features/Onboarding/components/OBNavigation/NavigationContext';
import OnboardingPageAdapter from 'features/Onboarding/components/OnboardingPageAdapter';
import BeneficiariesDocs from 'features/Onboarding/legacy/Pages/BeneficiariesDocs';
import DownloadShineMessageSent from 'features/Onboarding/legacy/Pages/DownloadShineMessageSent';
import CapitalDepositEUBankAccount from 'features/Onboarding/modules/CapitalDepositEUBankAccount';
import Mandate from 'features/Onboarding/modules/Mandate';
import CapitalDepositNextSteps from 'features/Onboarding/pages/CapitalDepositNextSteps';
import CompanyName from 'features/Onboarding/pages/CompanyName';

import OBLoaderPage from '../../components/OBLoaderPage/OBLoaderPage';
import useOverrideSetCurrentPage from '../../hooks/useOverrideSetCurrentPage';
import { User } from '../../legacy/types';
import Beneficiaries from '../../modules/Beneficiaries';
import CapitalDepositQuestions from '../../modules/CapitalDepositQuestions';
import CompanyCompliance from '../../modules/CompanyCompliance';
import Verification from '../../modules/Verification';
import SuccessMandatee from '../../pages/SuccessMandatee';
import { ApplicantType, ApplicationType } from '../../types';
import { CapitalDepositMandateeModuleDocument } from './graphql/queries/capitalDepositMandateeModule.gql';
import machineConfig, { model, State } from './machine';
import getNavigationSteps from './navigation/getNavigationSteps';
import CompanyInformationIntro from './pages/CompanyInformationIntro';
import PersonalInformationIntro from './pages/PersonalInformationIntro';

const OnboardingCapitalDepositMandateeInternalComponent: BentoModuleComponent<
  typeof model
> = ({ machine, moduleData }) => {
  const [state, send] = machine;

  const stringifiedState = stringifyState<State>(state);

  const onPrev = useCallback(() => send(model.events.PREV()), [send]);

  const adaptedPageProps = {
    onPrev,
    setCurrentPage: useOverrideSetCurrentPage(send),
    user: moduleData.data.viewer.profile as unknown as User,
  };

  const { companyProfileId } = moduleData.companyContext;

  switch (stringifiedState) {
    case State.COMPANY_NAME:
    case State.CAPITAL_DEPOSIT_CREATE:
    case State.UPDATE_COMPANY_NAME:
      return (
        <CompanyName
          companyProfileId={companyProfileId}
          isSending={[
            State.CAPITAL_DEPOSIT_CREATE,
            State.UPDATE_COMPANY_NAME,
          ].some((s) => state.matches(s))}
          onPrev={onPrev}
          onSubmit={(values) => send(model.events.SUBMIT_COMPANY_NAME(values))}
        />
      );

    case State.MANDATE: {
      if (companyProfileId === undefined) {
        throw new Error(`State ${stringifiedState} reached without a company`);
      }

      return (
        <Mandate
          companyProfileId={companyProfileId}
          onDone={({ status }) =>
            status === BentoModuleDoneStatus.DONE
              ? send(model.events.NEXT())
              : onPrev()
          }
        />
      );
    }

    case State.COMPANY_INFORMATION_INTRO:
      return (
        <CompanyInformationIntro
          onNext={() => send(model.events.NEXT())}
          onPrev={onPrev}
        />
      );

    case State.EU_BANK_ACCOUNT:
      return (
        <CapitalDepositEUBankAccount
          isMandatee
          onDone={({ status }) =>
            status === BentoModuleDoneStatus.DONE
              ? send(model.events.NEXT())
              : onPrev()
          }
        />
      );

    case State.COMPANY_COMPLIANCE: {
      if (!companyProfileId) {
        throw new Error(`State ${stringifiedState} reached without a company`);
      }

      return (
        <CompanyCompliance
          companyProfileId={companyProfileId}
          isMandatee
          onDone={({ status }) =>
            status === BentoModuleDoneStatus.DONE
              ? send(model.events.NEXT())
              : onPrev()
          }
        />
      );
    }

    case State.CAPITAL_DEPOSIT_QUESTIONS: {
      if (!companyProfileId) {
        throw new Error(`State ${stringifiedState} reached without a company`);
      }

      return (
        <CapitalDepositQuestions
          companyProfileId={companyProfileId}
          isMandatee
          onDone={({ status }) =>
            status === BentoModuleDoneStatus.DONE
              ? send(model.events.NEXT())
              : onPrev()
          }
        />
      );
    }

    case State.BENEFICIARIES: {
      if (!companyProfileId) {
        throw new Error(`State ${stringifiedState} reached without a company`);
      }
      return (
        <Beneficiaries
          applicantType={ApplicantType.Mandatee}
          applicationType={ApplicationType.CapitalDeposit}
          companyProfileId={companyProfileId}
          onDone={({ status }) =>
            status === BentoModuleDoneStatus.DONE
              ? send(model.events.NEXT())
              : onPrev()
          }
        />
      );
    }

    case State.PERSONAL_INFORMATION_INTRO:
      return (
        <PersonalInformationIntro
          onNext={() => send(model.events.NEXT())}
          onPrev={onPrev}
        />
      );

    case State.VERIFICATION: {
      if (!companyProfileId || !moduleData.data.viewer.companyUser) {
        throw new Error(`State ${stringifiedState} reached without a company`);
      }

      return (
        <Verification
          applicantType={ApplicantType.Mandatee}
          applicationType={ApplicationType.CapitalDeposit}
          companyProfileId={companyProfileId}
          isMainApplicant
          onDone={({ status }) =>
            status === BentoModuleDoneStatus.DONE
              ? send(model.events.NEXT())
              : onPrev()
          }
        />
      );
    }

    case State.AGREEMENT: {
      return (
        <Agreement
          onDone={({ status }) =>
            status === BentoModuleDoneStatus.DONE
              ? send(model.events.NEXT())
              : onPrev()
          }
          type={OutstandingAgreementType.Cgu}
        />
      );
    }

    case State.SUCCESS:
      return (
        <SuccessMandatee
          isNewOnboardingAllowed={moduleData.data.viewer.isNewOnboardingAllowed}
          onNext={() => send(model.events.NEXT())}
          onPrev={onPrev}
        />
      );

    case State.CAPITAL_DEPOSIT_NEXT_STEPS:
      return (
        <CapitalDepositNextSteps
          goToBeneficiaries={() => send('GO_BENEFICIARIES_DOCS')}
          goToSmsSent={() => send('DOWNLOAD_SHINE')}
          onPrev={onPrev}
        />
      );

    case State.BENEFICIARIES_DOCS:
      return (
        <OnboardingPageAdapter
          {...adaptedPageProps}
          LegacyOnboardingPage={BeneficiariesDocs}
        />
      );

    case State.DOWNLOAD_SHINE_MESSAGE_SENT:
      return (
        <OnboardingPageAdapter
          {...adaptedPageProps}
          LegacyOnboardingPage={DownloadShineMessageSent}
        />
      );

    case State.INIT:
    case State.SEND_APPLICATION:
    case State.DONE:
    case State.ABORT:
      return <OBLoaderPage onPrev={() => null} />;

    default:
      return assertNever(stringifiedState);
  }
};

const OnboardingCapitalDepositMandateeComponent = withStateMachine<
  typeof model
>(
  ({ machine, moduleData }) => {
    const [state] = machine;
    const legalName = moduleData.data.viewer.company?.profile.legalName || null;
    const { companyUser, metadata: userMetadata } = moduleData.data.viewer;

    // Rely on userMetadata.isMandatee only when we don’t have a company yet
    const isMandatee = companyUser?.isMandatee ?? userMetadata.isMandatee;

    if (!isMandatee) {
      throw new Error(
        'CapitalDepositMandatee module accessed by a non-mandatee',
      );
    }

    return (
      <NavigationContext.Provider
        value={{
          steps: getNavigationSteps({
            legalName,
            state: stringifyState<State>(state),
          }),
        }}
      >
        <OnboardingCapitalDepositMandateeInternalComponent
          machine={machine}
          moduleData={moduleData}
        />
      </NavigationContext.Provider>
    );
  },

  {
    devTools: true,
    machineConfig,
  },
);

const OnboardingCapitalDepositMandatee = asBentoModule(({ onDone }) => {
  const companyContext = useCompanyContext();
  const { companyProfileId } = companyContext;
  const query = useQuery(CapitalDepositMandateeModuleDocument, {
    variables: {
      companyProfileId: companyProfileId ?? '',
      includeCompany: Boolean(companyProfileId),
    },
  });

  return (
    <SuspenseQuery loader={<OBLoaderPage onPrev={() => null} />} query={query}>
      {(data) => (
        <OnboardingCapitalDepositMandateeComponent
          moduleData={{ companyContext, data }}
          onDone={onDone}
        />
      )}
    </SuspenseQuery>
  );
});

export default OnboardingCapitalDepositMandatee;
